import { Box, Text } from '@mantine/core';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { ClientActionsMenu } from 'src/clients/components/ClientActionsMenu';
import { useDeleteClient } from 'src/clients/hooks/useDeleteClient';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import { useRemoveFamilyMember } from 'src/clients/hooks/useRemoveFamilyMember';
import FamilyConfirmModal from 'src/non-financial-data/v2/family/FamilyConfirmModal';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { SortableColumn } from 'src/common/components/table/types';
import { useGlobalConfirmation } from 'src/design-system/GlobalConfirmationProvider';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

type Props = {
  familyId: string;
};

export function FamilyMembersTable({ familyId }: Props) {
  const { family } = useFamily(familyId);
  const { familyRoles } = useFamilyRoles();
  const {
    deleteClient,
    error: deleteError,
    isLoading: isDeleting,
    reset: resetDelete,
  } = useDeleteClient();
  const {
    removeFamilyMember,
    error: removeError,
    isLoading: isRemoving,
    reset: resetRemove,
  } = useRemoveFamilyMember();
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [clientToRemove, setClientToRemove] = useState<Client | null>(null);
  const { showConfirmation } = useGlobalConfirmation();

  function handleDeletePress(client: Client) {
    resetDelete();
    setClientToDelete(client);
  }

  async function handleDeleteConfirm() {
    if (!clientToDelete) return;
    const deletedFirstName = formatName(
      clientToDelete.firstName,
      clientToDelete.lastName,
      NameFormat.FIRST_ONLY,
    );
    try {
      await deleteClient(clientToDelete.id);
      setClientToDelete(null);
      showConfirmation(`${deletedFirstName}'s profile deleted`);
    } catch {
      // error surfaced via deleteError
    }
  }

  function handleRemovePress(client: Client) {
    resetRemove();
    setClientToRemove(client);
  }

  async function handleRemoveConfirm() {
    if (!clientToRemove) return;
    const removedName = formatName(
      clientToRemove.firstName,
      clientToRemove.lastName,
      NameFormat.NATURAL,
    );
    try {
      await removeFamilyMember(familyId, clientToRemove.id);
      setClientToRemove(null);
      showConfirmation(`${removedName} has been removed`);
    } catch {
      // error surfaced via removeError
    }
  }

  const roleNameById = new Map(familyRoles.map(role => [role.id, role.name]));
  const roleIdByClientId = new Map(
    (family?.members ?? []).map(member => [member.clientId, member.familyRoleId]),
  );

  const columnsBase: SortableColumn<Client>[] = [
    {
      accessor: 'name',
      title: 'Member',
      render: (client: Client) => (
        <Box>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
          </Text>
          {client.emailAddress && (
            <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
              {client.emailAddress}
            </Text>
          )}
        </Box>
      ),
    },
    {
      accessor: 'role',
      title: 'Role',
      render: (client: Client) => {
        const roleId = roleIdByClientId.get(client.id);
        const roleName = roleId ? roleNameById.get(roleId) : null;
        return (
          <Text size="sm" fw={FONT_WEIGHTS.medium} c={roleName ? 'black' : 'gray.6'}>
            {roleName ?? '—'}
          </Text>
        );
      },
    },
    {
      accessor: 'actions',
      title: '',
      render: (client: Client) => (
        <ClientActionsMenu
          client={client}
          onDelete={() => handleDeletePress(client)}
          onRemoveFromFamily={() => handleRemovePress(client)}
          iconColor="var(--mantine-color-black)"
        />
      ),
    },
  ];

  return (
    <>
      <SimpleTable<Client>
        resource="clients"
        filters={{ familyId }}
        pageSize={9999}
        columns={columnsBase}
        variant="new-flow"
        enableSearch={false}
      />
      <FamilyConfirmModal
        opened={!!clientToDelete}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${formatName(clientToDelete?.firstName, clientToDelete?.lastName, NameFormat.NATURAL)}?`}
        description={`This will permanently delete ${formatName(clientToDelete?.firstName, clientToDelete?.lastName, NameFormat.FIRST_ONLY)}'s profile, including all their financial data, documents and meeting history. This action cannot be undone.`}
        confirmLabel="Delete client"
        error={deleteError}
        isLoading={isDeleting}
      />
      <FamilyConfirmModal
        opened={!!clientToRemove}
        onClose={() => setClientToRemove(null)}
        onConfirm={handleRemoveConfirm}
        title={`Remove ${formatName(clientToRemove?.firstName, clientToRemove?.lastName, NameFormat.NATURAL)} from family?`}
        description={`${formatName(clientToRemove?.firstName, clientToRemove?.lastName, NameFormat.FIRST_ONLY)} will become an individual client. Their profile, financial data, documents and meetings history will remain intact.`}
        confirmLabel="Remove from family"
        error={removeError}
        isLoading={isRemoving}
      />
    </>
  );
}
